import React, { useState, useEffect, useMemo } from 'react';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle 
} from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from './ui/select';
import { 
  Search, 
  Download, 
  Eye, 
  CheckCircle, 
  AlertCircle, 
  Clock, 
  XCircle,
  Users,
  ArrowLeft,
  Bell,
  Loader2
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from './ui/dialog';
import { api } from '../services/api';
import SuiviPaiementDetailsModal from './SuiviPaiementDetailsModal';
import LoadingSpinner from './LoadingSpinner';
import { Toast } from '../components/ui/Toast';

// Les deux boutons d'un pied de modal ont la meme taille ET la meme largeur.
// DESIGN-SYSTEM.md, section Boutons.
const BOUTON_PIED = 'h-9 min-w-[168px] justify-center gap-2';

/**
 * Ce qu'un SMS coûte réellement, et ce que le texte occupe.
 *
 * Le franc CFA est en parité FIXE avec l'euro depuis 1999 : 1 € = 655,957
 * FCFA. Le prix OVH d'un SMS vers le Congo est de 0,20 € HT — la même
 * constante existe côté serveur, qui reste seul juge au moment d'envoyer.
 */
const PRIX_SMS_EUR = 0.20;
const PARITE_EUR_FCFA = 655.957;
const coutFcfa = (n) => Math.round(n * PRIX_SMS_EUR * PARITE_EUR_FCFA);

// Jeu de caractères GSM-7. Tout ce qui n'y est pas fait basculer le message
// ENTIER en UCS-2, où la limite tombe de 160 à 70 caractères — et le prix
// double. Un `ê`, un `ô`, une apostrophe courbe suffisent.
const GSM7 = '@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&\'()*+,-./0123456789:;<=>?¡'
  + 'ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà';
const GSM7_EXT = '^{}\\[~]|€';

const mesurerSms = (texte) => {
  let longueur = 0;
  const interdits = [];

  for (const c of texte) {
    if (GSM7.includes(c)) longueur += 1;
    else if (GSM7_EXT.includes(c)) longueur += 2;
    else if (!interdits.includes(c)) interdits.push(c);
  }

  if (interdits.length > 0) {
    // Un seul caractère hors GSM-7 et TOUT le message passe en UCS-2.
    return {
      encodage: 'UCS-2',
      longueur: texte.length,
      limite: 70,
      segments: Math.ceil(texte.length / 70) || 1,
      interdits,
    };
  }

  return {
    encodage: 'GSM-7',
    longueur,
    limite: 160,
    segments: longueur <= 160 ? 1 : Math.ceil(longueur / 153),
    interdits: [],
  };
};

// Une date d'envoi telle qu'on la lit dans un message : « 13/09 à 21:13 ».
const jourDeRelance = (valeur) => {
  if (!valeur) return null;
  const [date, heure] = String(valeur).split(/[T ]/);
  const [, m, j] = (date || '').split('-');
  return m && j ? `${j}/${m}${heure ? ` à ${heure.slice(0, 5)}` : ''}` : null;
};

const SuiviPaiementScolaire = ({ onClose }) => {
  // États essentiels
  const [elevesBruts, setElevesBruts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSection, setFilterSection] = useState('all');
  const [filterStatut, setFilterStatut] = useState('all');
  const [filterTrimestre, setFilterTrimestre] = useState('1');
  const [filterMois, setFilterMois] = useState('all');
  const [isLoading, setIsLoading] = useState(true);
  const [anneeActive, setAnneeActive] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedEleveForDetails, setSelectedEleveForDetails] = useState(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [filterClasse, setFilterClasse] = useState('all');
  // Les quatre sections etaient ecrites en dur dans le filtre, ET leurs
  // identifiants dans `sectionIdMap`. L'ecran « Classes et matieres » permet
  // desormais d'en creer et d'en supprimer : une cinquieme aurait ete
  // invisible ici, et en renommer une aurait casse la correspondance.
  const [sectionsDisponibles, setSectionsDisponibles] = useState([]);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  // Une liste vide et une liste qu'on n'a pas pu lire ne se ressemblent pas.
  // Elles s'affichaient pourtant a l'identique : « Aucun eleve trouve ». Sur un
  // tableau de creances, c'est le pire des resultats.
  const [erreurChargement, setErreurChargement] = useState(null);
  // La relance groupée : l'aperçu d'abord, l'envoi ensuite. `apercu` vaut
  // `null` tant que le serveur n'a pas répondu — on n'affiche jamais un nombre
  // deviné à côté d'un bouton qui dépense de l'argent.
  // L'élève en cours de relance, `null` quand le modal est fermé.
  const [eleveARelancer, setEleveARelancer] = useState(null);
  const [apercu, setApercu] = useState(null);
  const [apercuEnCours, setApercuEnCours] = useState(false);
  const [envoiEnCours, setEnvoiEnCours] = useState(false);
  // Le texte tel qu'il partira. Pré-rempli avec la proposition du serveur,
  // modifiable ensuite.
  const [texteSms, setTexteSms] = useState('');
  
  const ITEMS_PER_PAGE = 20;

  // Charger l'année scolaire active
  useEffect(() => {
    const fetchAnneeActive = async () => {
      try {
        const response = await api.anneesScolaires.getActive();
        if (!response?.success || !response.data) {
          throw new Error(response?.message || response?.error
            || "Aucune année scolaire active n'a pu être lue.");
        }
        setAnneeActive(response.data);
        setErreurChargement(null);
      } catch (error) {
        // Sans année, `fetchAllData` sortait avant son `setIsLoading(false)` :
        // `isLoading` démarrant à `true`, la page tournait indéfiniment sans
        // qu'aucun message n'explique pourquoi.
        console.error('[Suivi] Année scolaire :', error);
        setErreurChargement(error.message);
        setIsLoading(false);
      }
    };
    fetchAnneeActive();
  }, []);

  useEffect(() => {
    const chargerSections = async () => {
      try {
        const reponse = await api.sections.getAll();
        setSectionsDisponibles(Array.isArray(reponse?.data) ? reponse.data : []);
      } catch (error) {
        // Le filtre se vide, la liste reste entiere : mieux vaut ne pas
        // pouvoir filtrer que de masquer des eleves sans le dire.
        console.error('[Suivi] Sections indisponibles :', error);
        setSectionsDisponibles([]);
      }
    };
    chargerSections();
  }, []);

  // 🚀 UN SEUL APPEL API pour charger TOUTES les données
  useEffect(() => {
    const fetchAllData = async () => {
      if (!anneeActive) return;
      
      try {
        setIsLoading(true);

        // UN SEUL appel qui récupère tout
        const response = await api.finance.getSuiviPaiementComplet(anneeActive.id, filterTrimestre);

        if (!response?.success || !Array.isArray(response.data)) {
          throw new Error(response?.message || response?.error
            || 'Le suivi des paiements n\'a pas pu être lu.');
        }

        setElevesBruts(response.data);
        setErreurChargement(null);
      } catch (error) {
        console.error('[Suivi] Chargement des paiements :', error);
        setElevesBruts([]);
        setErreurChargement(error.message);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchAllData();
  }, [anneeActive, filterTrimestre]);

  // Obtenir les mois disponibles depuis les données
  const moisDisponibles = useMemo(() => {
    if (elevesBruts.length === 0) return [];
    
    const moisSet = new Set();
    elevesBruts.forEach(eleve => {
      eleve.paiements.forEach(p => {
        if (p.trimestre === parseInt(filterTrimestre)) {
          moisSet.add(JSON.stringify({
            id: p.mois_id,
            nom: p.mois_nom,
            ordre: p.mois_ordre,
            trimestre: p.trimestre
          }));
        }
      });
    });
    
    return Array.from(moisSet)
      .map(s => JSON.parse(s))
      .sort((a, b) => a.ordre - b.ordre);
  }, [elevesBruts, filterTrimestre]);

  // Obtenir les classes disponibles
  const classesDisponibles = useMemo(() => {
    const classesMap = new Map();
    elevesBruts.forEach(eleve => {
      if (!classesMap.has(eleve.classe_id)) {
        classesMap.set(eleve.classe_id, {
          id: eleve.classe_id,
          nom: eleve.classe_nom,
          section_id: eleve.section_id
        });
      }
    });
    return Array.from(classesMap.values()).sort((a, b) => a.nom.localeCompare(b.nom));
  }, [elevesBruts]);

  // 🚀 CALCUL CÔTÉ CLIENT des statuts et montants (ULTRA RAPIDE)
  const elevesAvecStatut = useMemo(() => {
    return elevesBruts.map(eleve => {
      let paiementsAAnalyser;
      if (filterMois === 'all') {
        paiementsAAnalyser = eleve.paiements.filter(p => p.trimestre === parseInt(filterTrimestre));
      } else {
        paiementsAAnalyser = eleve.paiements.filter(p => p.mois_id === parseInt(filterMois));
      }

      let mois_payes = 0;
      let mois_non_payes = 0;
      let moisNonScolarises = 0;
      let montant_total_du = 0;
      let montant_total_paye = 0;

      let moisSoldes = 0;

      paiementsAAnalyser.forEach(p => {
        if (p.statut_frequentation === 'non_scolarise') {
          moisNonScolarises++;
          return;
        }

        const montantDu = p.montant_attendu || 0;
        const montantPaye = p.montant_paye || 0;

        // Mois "Soldé" = payé au site source, compter comme payé
        if (p.statut === 'Soldé') {
          mois_payes++;
          moisSoldes++;
          montant_total_du += montantDu;
          montant_total_paye += montantPaye;
          return;
        }

        montant_total_du += montantDu;
        montant_total_paye += montantPaye;

        if (montantPaye >= montantDu && montantDu > 0) {
          mois_payes++;
        } else {
          mois_non_payes++;
        }
      });

      const total_mois = paiementsAAnalyser.length - moisNonScolarises;
      const montant_du = montant_total_du - montant_total_paye;

      // ----------------------------------------------------------------
      // Le statut, exprimé en PART du trimestre et non en nombre de mois
      // ----------------------------------------------------------------
      // Les seuils étaient écrits pour un trimestre de trois mois :
      //
      //     } else if (mois_payes === 1 && total_mois === 3) {        // urgente
      //     } else if (mois_payes === 2 && … && total_mois === 3) {   // moins urgent
      //     } else if (mois_payes >= 2 || mois_payes >= total_mois) { // à jour
      //
      // Vérifié en base : toutes les années de toutes les écoles sont
      // aujourd'hui en 3/3/3, donc rien n'était faux à l'écran. Mais
      // CALENDRIER-DE-PAIE.md pose que le calendrier de l'école est la seule
      // source de vérité, et l'écran de calendrier permet désormais d'en
      // changer le découpage. Avec quatre mois, les deux branches du milieu
      // ne se déclenchaient plus : un élève ayant payé deux mois sur quatre
      // retombait sur `mois_payes >= 2` et s'affichait « À jour », en vert.
      //
      // Les bornes ci-dessous rendent EXACTEMENT le même classement qu'avant
      // sur trois mois — 0 critique, 1 urgente, 2 moins urgent, 3 à jour —
      // et restent justes sur n'importe quel autre découpage.
      let statut = 'a_jour';
      let flag = 'green';

      if (total_mois === 0) {
        statut = 'a_jour';
        flag = 'green';
      } else if (filterMois !== 'all') {
        // Un seul mois à l'écran : payé ou non, il n'y a pas de nuance.
        if (mois_payes >= 1) {
          statut = 'a_jour';
          flag = 'green';
        } else {
          statut = 'dette_critique';
          flag = 'black';
        }
      } else {
        const part = mois_payes / total_mois;

        if (mois_payes === 0) {
          statut = 'dette_critique';
          flag = 'black';
        } else if (part < 1 / 2) {
          statut = 'dette_urgente';
          flag = 'red';
        } else if (part < 1) {
          statut = 'dette_moins_urgente';
          flag = 'yellow';
        } else {
          statut = 'a_jour';
          flag = 'green';
        }
      }

      return {
        ...eleve,
        mois_payes,
        mois_non_payes,
        mois_soldes: moisSoldes,
        mois_non_scolarises: moisNonScolarises,
        total_mois,
        statut,
        flag,
        montant_du,
        montant_total: montant_total_du,
        trimestre: parseInt(filterTrimestre)
      };
      
    });
  }, [elevesBruts, filterTrimestre, filterMois]);

  // Filtrage côté client
  const elevesFiltres = useMemo(() => {
    return elevesAvecStatut.filter(eleve => {
      const matchSearch = searchTerm === '' ||
        eleve.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
        eleve.prenom.toLowerCase().includes(searchTerm.toLowerCase()) ||
        eleve.classe_nom.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchSection = filterSection === 'all' || eleve.section_nom === filterSection;
      const matchStatut = filterStatut === 'all' || eleve.statut === filterStatut;
      const matchClasse = filterClasse === 'all' || eleve.classe_nom === filterClasse;
      
      return matchSearch && matchSection && matchStatut && matchClasse;
    });
  }, [elevesAvecStatut, searchTerm, filterSection, filterStatut, filterClasse]);

  // Statistiques dynamiques
  const stats = useMemo(() => ({
    total: elevesFiltres.length,
    a_jour: elevesFiltres.filter(e => e.statut === 'a_jour').length,
    dette_urgente: elevesFiltres.filter(e => e.statut === 'dette_urgente').length,
    dette_moins_urgente: elevesFiltres.filter(e => e.statut === 'dette_moins_urgente').length,
    dette_critique: elevesFiltres.filter(e => e.statut === 'dette_critique').length,
    montant_total_du: elevesFiltres.reduce((sum, e) => sum + (e.montant_du || 0), 0)
  }), [elevesFiltres]);

  // Les bornes de la légende, déduites du trimestre réel et non écrites en
  // dur. `moisDisponibles` vient des paiements eux-mêmes, donc du calendrier
  // de l'école.
  const bornesLegende = useMemo(() => {
    const n = moisDisponibles.length;
    if (n === 0) return { aJour: '—', moinsUrgent: '—', urgente: '—' };

    // Les mêmes bornes que le classement, lues à l'envers :
    //   urgente      1 … le dernier compte encore sous la moitié
    //   moins urgent de là jusqu'à n - 1
    //   à jour       n
    const urgenteMax = Math.ceil(n / 2) - 1;
    const moinsUrgentMin = urgenteMax + 1;

    const plage = (min, max) => {
      if (min > max || max < 1) return 'ne peut pas se produire ici';
      if (min === max) return `${min} mois payé${min > 1 ? 's' : ''} sur ${n}`;
      return `${min} à ${max} mois payés sur ${n}`;
    };

    return {
      aJour: `${n} mois payé${n > 1 ? 's' : ''} sur ${n}`,
      moinsUrgent: plage(moinsUrgentMin, n - 1),
      urgente: plage(1, urgenteMax)
    };
  }, [moisDisponibles]);

  // Pagination
  const totalPages = Math.ceil(elevesFiltres.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const elevesPage = elevesFiltres.slice(startIndex, endIndex);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterSection, filterStatut, filterClasse, filterMois]);

  useEffect(() => {
    setFilterClasse('all');
  }, [filterSection]);

  useEffect(() => {
    setFilterMois('all');
  }, [filterTrimestre]);

  const formatMontant = (montant) => {
    return `${Math.round(montant).toLocaleString('fr-FR').replace(/,/g, ' ')} FCFA`;
  };

  const getStatutBadge = (statut) => {
    const configs = {
      a_jour: { icon: CheckCircle, text: "À jour", bg: "bg-green-100", color: "text-green-800" },
      dette_moins_urgente: { icon: Clock, text: "Moins urgent", bg: "bg-yellow-100", color: "text-yellow-800" },
      dette_urgente: { icon: AlertCircle, text: "Urgente", bg: "bg-red-100", color: "text-red-800" },
      // Le noir plein etait la seule note de ce genre dans KLASYS, et il
      // tranchait avec les trois autres badges. Le rouge fonce dit la meme
      // urgence en restant dans la palette.
      dette_critique: { icon: XCircle, text: "Critique", bg: "bg-red-100", color: "text-red-900" }
    };
    
    // Sans ce repli, une valeur inattendue faisait planter TOUT le tableau :
    // `configs[statut].icon` sur `undefined`.
    const config = configs[statut] || {
      icon: AlertCircle, text: statut || 'Inconnu',
      bg: 'bg-gray-100', color: 'text-gray-600'
    };
    const Icon = config.icon;
    
    // Largeur fixe : le badge suivait la longueur du texte, et « Moins
    // urgent » faisait deux fois « À jour ». D'une ligne a l'autre, la colonne
    // partait en accordeon. DESIGN-SYSTEM.md, section Badges :
    // `w-[132px] h-6`, des qu'un badge se repete.
    return (
      <span className={`inline-flex h-6 w-[132px] items-center justify-center gap-1 rounded-full text-xs font-medium whitespace-nowrap ${config.bg} ${config.color}`}>
        <Icon size={12} className="shrink-0" />
        {config.text}
      </span>
    );
  };

  const getFlag = (flag) => {
    const flags = {
      green: <CheckCircle className="text-green-500" size={20} />,
      yellow: <Clock className="text-yellow-500" size={20} />,
      red: <AlertCircle className="text-red-500" size={20} />,
      black: <XCircle className="text-gray-700" size={20} />
    };
    return flags[flag] || <span className="text-gray-300">—</span>;
  };

  const getClassesBySection = () => {
    if (filterSection === 'all') return classesDisponibles;

    // La correspondance nom -> identifiant vient de la base, plus d'une table
    // ecrite a la main.
    const sectionIdMap = Object.fromEntries(
      sectionsDisponibles.map((s) => [s.nom, s.id])
    );
    
    const sectionId = sectionIdMap[filterSection];
    return classesDisponibles.filter(c => c.section_id === sectionId);
  };

  // `handleExportCSV` vivait ici. Son bouton etait commente dans le rendu :
  // la fonction n'etait joignable par aucun chemin, et elle portait les deux
  // derniers `alert()` du fichier. L'export PDF fait le meme travail, lui
  // avec un `Toast`.



 const handleExportPDF = async () => {
  try {
    setToast({ 
      show: true, 
      message: 'Génération du PDF en cours... Patience, cela peut prendre quelques secondes.', 
      type: 'loading' 
    });
    
    const filtresFormates = {
      'Section': filterSection === 'all' ? 'Toutes' : filterSection,
      'Classe': filterClasse === 'all' ? 'Toutes' : filterClasse,
      'Statut': filterStatut === 'all' ? 'Tous' : filterStatut.replace(/_/g, ' ')
    };

    await api.finance.genererSuiviPaiementPDF(
      anneeActive.id,
      filterTrimestre,
      filterMois,
      filtresFormates
    );

    setToast({ 
      show: true, 
      message: `Rapport PDF généré avec succès ! ${elevesFiltres.length} élèves exportés`, 
      type: 'success' 
    });

  } catch (error) {
    console.error('❌ Erreur export PDF:', error);
    setToast({ 
      show: true, 
      message: `❌ Erreur: ${error.message}`, 
      type: 'error' 
    });
  }
};


  /**
   * L'aperçu avant d'envoyer : le message exact, et ce qui empêche l'envoi.
   *
   * Le même calcul que l'envoi, côté serveur. Le parent visé peut avoir
   * plusieurs enfants en retard : le message le dira, et un seul SMS partira
   * pour toute la fratrie — d'où l'aperçu plutôt qu'un envoi direct.
   */
  const ouvrirRelance = async (eleve) => {
    if (!anneeActive || !eleve) return;
    setApercu(null);
    setEleveARelancer(eleve);
    setApercuEnCours(true);
    try {
      const reponse = await api.finance.apercuRelances({
        annee_scolaire_id: anneeActive.id,
        trimestre: parseInt(filterTrimestre, 10),
        mois_id: filterMois !== 'all' ? parseInt(filterMois, 10) : null,
        eleve_ids: [eleve.id]
      });
      if (reponse?.success) {
        setApercu(reponse.data);
        setTexteSms(reponse.data.apercu?.[0]?.message || '');
      } else {
        setEleveARelancer(null);
        setToast({
          show: true,
          message: reponse?.message || reponse?.error || 'Impossible de préparer la relance',
          type: 'error'
        });
      }
    } catch (e) {
      setEleveARelancer(null);
      setToast({ show: true, message: e.message, type: 'error' });
    } finally {
      setApercuEnCours(false);
    }
  };

  const envoyerRelance = async () => {
    if (!anneeActive || !apercu || !eleveARelancer) return;
    setEnvoiEnCours(true);
    try {
      const reponse = await api.finance.envoyerRelances({
        annee_scolaire_id: anneeActive.id,
        trimestre: parseInt(filterTrimestre, 10),
        mois_id: filterMois !== 'all' ? parseInt(filterMois, 10) : null,
        eleve_ids: [eleveARelancer.id],
        // Le serveur recompte les segments : cette limite-ci n'est qu'un
        // confort d'affichage.
        message: texteSms.trim() || null
      });
      if (reponse?.success) {
        const eleve = eleveARelancer;
        setEleveARelancer(null);
        setToast({ show: true, message: reponse.message, type: 'success' });
        // On rouvre la fiche : c'est de la qu'on est parti, et son historique
        // porte maintenant une ligne de plus. La laisser fermee obligerait a
        // rechercher l'eleve pour verifier ce qui s'est passe.
        setSelectedEleveForDetails(eleve);
        setIsDetailsModalOpen(true);
      } else {
        setToast({
          show: true,
          message: reponse?.message || reponse?.error || "L'envoi a échoué",
          type: 'error'
        });
      }
    } catch (e) {
      setToast({ show: true, message: e.message, type: 'error' });
    } finally {
      setEnvoiEnCours(false);
    }
  };

  const handleViewDetails = (eleve) => {
    setSelectedEleveForDetails(eleve);
    setIsDetailsModalOpen(true);
  };

  /**
   * La relance n'est PAS branchée.
   *
   * Elle faisait `alert('Rappel envoyé à …')` sans le moindre appel serveur :
   * l'écran affirmait un envoi qui n'avait pas lieu, et la même fonction était
   * passée au modal de détails — deux boutons pour un même mensonge. On peut
   * relancer trente familles et croire le travail fait.
   *
   * Le canal retenu est le SMS ; le circuit reste à définir. En attendant, le
   * bouton dit ce qu'il en est plutôt que le contraire.
   */
  /**
   * Depuis la fiche : on ferme le détail, puis on ouvre l'aperçu de relance.
   *
   * Deux dialogues empilés se disputent le piège de focus et le clavier ; on
   * passe de l'un à l'autre plutôt que de les superposer.
   */
  const handleSendReminder = (eleve) => {
    setIsDetailsModalOpen(false);
    ouvrirRelance(eleve);
  };

  return (
    <div className="w-full">
      <Card className="border-0 shadow-sm">
        <CardHeader className="bg-[#246990] text-white rounded-t-lg">
          {onClose && (
            <Button variant="ghost" className="mb-3 text-white hover:bg-white/20 w-fit" onClick={onClose}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour à la gestion des paiements
            </Button>
          )}
          
          <div className="flex items-center justify-between">
           <CardTitle className="font-bold">Suivi des Paiements Scolaires</CardTitle>
            
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-1 flex items-center gap-1">
              {[1, 2, 3].map(t => (
                <button
                  key={t}
                  onClick={() => setFilterTrimestre(t.toString())}
                  className={`px-4 py-2 rounded-md font-semibold transition-all text-sm ${
                    filterTrimestre === t.toString()
                      ? 'bg-white text-[#246990] shadow-md'
                      : 'text-white hover:bg-white/20'
                  }`}
                >
                  Trimestre {t}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-4 flex items-center gap-2">
            <span className="text-sm font-medium text-white/90">Filtrer par mois :</span>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-1 flex items-center gap-1 flex-wrap">
              <button
                onClick={() => setFilterMois('all')}
                className={`px-3 py-1.5 rounded-md font-medium transition-all text-sm ${
                  filterMois === 'all'
                    ? 'bg-white text-[#246990] shadow-md'
                    : 'text-white hover:bg-white/20'
                }`}
              >
                Tout le trimestre
              </button>
              {moisDisponibles.map(m => (
                <button
                  key={m.id}
                  onClick={() => setFilterMois(m.id.toString())}
                  className={`px-3 py-1.5 rounded-md font-medium transition-all text-sm ${
                    filterMois === m.id.toString()
                      ? 'bg-white text-[#246990] shadow-md'
                      : 'text-white hover:bg-white/20'
                  }`}
                >
                  {m.nom}
                </button>
              ))}
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-6">
          {/* Statistiques */}
          <div className="grid grid-cols-5 gap-4 mb-6">
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-600 font-medium">Total élèves</p>
                  <p className="text-2xl font-bold text-blue-900">{stats.total}</p>
                </div>
                <Users size={30} className="text-blue-500" />
              </CardContent>
            </Card>
            
            <Card className="bg-green-50 border-green-200">
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-600 font-medium">À jour</p>
                  <p className="text-2xl font-bold text-green-900">{stats.a_jour}</p>
                </div>
                <CheckCircle size={30} className="text-green-500" />
              </CardContent>
            </Card>
            
            <Card className="bg-yellow-50 border-yellow-200">
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <p className="text-sm text-yellow-600 font-medium">Moins urgent</p>
                  <p className="text-2xl font-bold text-yellow-900">{stats.dette_moins_urgente}</p>
                </div>
                <Clock size={30} className="text-yellow-500" />
              </CardContent>
            </Card>
            
            <Card className="bg-red-50 border-red-200">
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <p className="text-sm text-red-600 font-medium">Urgente</p>
                  <p className="text-2xl font-bold text-red-900">{stats.dette_urgente}</p>
                </div>
                <AlertCircle size={30} className="text-red-500" />
              </CardContent>
            </Card>
            
            {/* La carte était `bg-gray-900` pleine, seule note noire de tout
                KLASYS. Le rouge foncé dit la même urgence sans sortir de la
                palette. */}
            <Card className="bg-red-100 border-red-300">
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-red-800">Critique</p>
                  <p className="text-2xl font-bold text-red-900">{stats.dette_critique}</p>
                </div>
                <XCircle size={30} className="text-red-700" />
              </CardContent>
            </Card>
          </div>

          {/* Montant total */}
          {/* La légende, juste sous les compteurs qu'elle explique.
              Elle vivait tout en bas, après la pagination : il fallait avoir
              fini de lire le tableau pour comprendre ce qu'on venait d'y voir.
              Compacte, pour ne pas repousser le tableau.

              Ses bornes se déduisent du nombre de mois réellement affiché.
              Le texte était écrit en dur — « 2-3 mois payés sur 3 » pour
              « À jour », ce qui était d'ailleurs faux : 2 sur 3 est classé
              « moins urgent ». */}
          <div className="mb-6 flex flex-wrap items-center gap-x-6 gap-y-2 rounded-lg border border-gray-200 bg-gray-50 px-4 py-2.5 text-xs">
            <span className="font-semibold uppercase tracking-wider text-gray-500">
              Lecture des statuts
            </span>
            <span className="flex items-center gap-1.5 text-gray-700">
              <CheckCircle className="shrink-0 text-green-500" size={15} />
              <strong className="font-medium text-gray-900">À jour</strong>
              {filterMois !== 'all' ? 'Mois payé' : bornesLegende.aJour}
            </span>
            {filterMois === 'all' && (
              <>
                <span className="flex items-center gap-1.5 text-gray-700">
                  <Clock className="shrink-0 text-yellow-500" size={15} />
                  <strong className="font-medium text-gray-900">Moins urgent</strong>
                  {bornesLegende.moinsUrgent}
                </span>
                <span className="flex items-center gap-1.5 text-gray-700">
                  <AlertCircle className="shrink-0 text-red-500" size={15} />
                  <strong className="font-medium text-gray-900">Urgente</strong>
                  {bornesLegende.urgente}
                </span>
              </>
            )}
            <span className="flex items-center gap-1.5 text-gray-700">
              <XCircle className="shrink-0 text-red-700" size={15} />
              <strong className="font-medium text-gray-900">Critique</strong>
              {filterMois !== 'all' ? 'Mois non payé' : 'Aucun mois payé'}
            </span>
          </div>

          {/* L'orange n'est pas dans la palette ; la teinte de la marque
              porte aussi bien un montant. Le bouton CSV commenté vivait ici. */}
          <Card className="mb-6 border-[#246990]/20 bg-[#246990]/5">
            <CardContent className="p-4">
              <div className="flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-[#246990]">
                    Montant total dû {filterMois !== 'all' && `(${moisDisponibles.find(m => m.id === parseInt(filterMois))?.nom})`}
                  </p>
                  <p className="text-3xl font-bold tabular-nums text-gray-900">
                    {formatMontant(stats.montant_total_du)}
                  </p>
                </div>
                {/* Un bouton de relance groupée vivait ici. La relance de
                    masse est un autre geste que consulter un tableau : elle
                    aura sa propre page de campagne. Depuis cet écran, on
                    relance depuis la fiche d'un élève. */}
                <Button
                  size="sm"
                  className="h-9 shrink-0 border border-transparent bg-[#246990] text-white hover:bg-[#1f5a7a]"
                  onClick={handleExportPDF}
                >
                  <Download size={16} className="mr-2" />
                  Télécharger la fiche
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Filtres */}
          <div className="flex flex-wrap gap-4 mb-6">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                <Input 
                  placeholder="Rechercher..." 
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            {/* Les sections viennent de la base : elles étaient écrites en
                dur, quatre options figées. */}
            <Select value={filterSection} onValueChange={setFilterSection}>
              <SelectTrigger className="h-9 w-[190px] border border-gray-200 bg-white focus:ring-2 focus:ring-[#246990]/15">
                <SelectValue placeholder="Toutes les sections" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes les sections</SelectItem>
                {sectionsDisponibles.map((s) => (
                  <SelectItem key={s.id} value={s.nom}>{s.nom}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {filterSection !== 'all' && (
              <Select value={filterClasse} onValueChange={setFilterClasse}>
                <SelectTrigger className="h-9 w-[190px] border border-gray-200 bg-white focus:ring-2 focus:ring-[#246990]/15">
                  <SelectValue placeholder="Toutes les classes" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes les classes</SelectItem>
                  {getClassesBySection().map((c) => (
                    <SelectItem key={c.id} value={c.nom}>{c.nom}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            <Select value={filterStatut} onValueChange={setFilterStatut}>
              <SelectTrigger className="h-9 w-[190px] border border-gray-200 bg-white focus:ring-2 focus:ring-[#246990]/15">
                <SelectValue placeholder="Tous les statuts" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                <SelectItem value="a_jour">À jour</SelectItem>
                <SelectItem value="dette_moins_urgente">Moins urgent</SelectItem>
                <SelectItem value="dette_urgente">Urgente</SelectItem>
                <SelectItem value="dette_critique">Critique</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Tableau */}
          {isLoading ? (
            <LoadingSpinner message="Chargement..." />
          ) : erreurChargement ? (
            /* « Il n'y a personne en retard » et « je n'ai pas pu regarder »
               sont deux phrases differentes. Elles s'affichaient pourtant a
               l'identique. */
            <div className="rounded-lg border border-red-200 bg-red-50 p-8 text-center">
              <AlertCircle size={40} className="mx-auto mb-3 text-red-400" />
              <h3 className="text-base font-semibold text-red-800">Lecture impossible</h3>
              <p className="mx-auto mt-1 max-w-md text-sm text-red-700">{erreurChargement}</p>
              <p className="mx-auto mt-2 max-w-md text-xs text-red-600">
                Des élèves peuvent être en retard de paiement : ne considérez pas
                cette liste comme vide.
              </p>
            </div>
          ) : elevesFiltres.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <AlertCircle size={48} className="mx-auto mb-3 text-gray-400" />
              <p>Aucun élève trouvé</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50">
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Flag</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Élève</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Classe</th>
                      <th className="w-[180px] px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Famille</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Section</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-gray-500">
                        {filterMois !== 'all' ? 'Payé' : 'Mois payés'}
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-gray-500">Soldé</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-gray-500">Non scolarisé</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-gray-500">Statut</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500">Montant dû</th>
                      <th className="w-[150px] px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-gray-500">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {elevesPage.map((eleve, index) => (
                      <tr
                        key={eleve.id}
                        className={`transition-colors hover:bg-gray-50 ${
                          index % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'
                        }`}
                      >
                        <td className="px-4 py-3">{getFlag(eleve.flag)}</td>
                        <td className="px-4 py-3">
                          <p className="font-semibold text-gray-900">{eleve.prenom} {eleve.nom}</p>
                        </td>
                        <td className="px-4 py-3 text-gray-700">{eleve.classe_nom}</td>

                        {/* 591 élèves sur 785 portent une famille. Pour les
                            autres, un tiret : c'est une information absente,
                            pas une famille vide. */}
                        <td className="px-4 py-3">
                          {eleve.famille_nom ? (
                            <>
                              <div className="truncate text-sm text-gray-900">{eleve.famille_nom}</div>
                              <div className="text-[11px] text-gray-400">
                                {eleve.famille_effectif} enfant{eleve.famille_effectif > 1 ? 's' : ''}
                              </div>
                            </>
                          ) : (
                            <span className="text-gray-300">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-gray-600">{eleve.section_nom}</td>
                        <td className="px-4 py-3 text-center">
                          {filterMois !== 'all' ? (
                            <span className={`inline-flex items-center gap-1 text-sm font-medium ${
                              eleve.mois_payes > 0 ? 'text-green-700' : 'text-red-700'
                            }`}>
                              {eleve.mois_payes > 0
                                ? <><CheckCircle size={13} /> Payé</>
                                : <><XCircle size={13} /> Non payé</>}
                            </span>
                          ) : (
                            <>
                              <span className="text-sm font-medium">{eleve.mois_payes}/{eleve.total_mois}</span>
                              <span className="text-xs text-gray-500 block">
                                ({eleve.mois_non_payes} non payé{eleve.mois_non_payes > 1 ? 's' : ''})
                              </span>
                            </>
                          )}
                        </td>

                        <td className="px-4 py-3 text-center">
                          {eleve.mois_soldes > 0 ? (
                            <span className="inline-flex h-6 w-[52px] items-center justify-center gap-1 rounded-full bg-teal-100 text-xs font-medium text-teal-700">
                              <CheckCircle size={12} className="shrink-0" />
                              {eleve.mois_soldes}
                            </span>
                          ) : (
                            <span className="inline-flex h-6 w-[52px] items-center justify-center text-gray-300">—</span>
                          )}
                        </td>

                        <td className="px-4 py-3 text-center">
                          {eleve.mois_non_scolarises > 0 ? (
                            <span className="inline-flex h-6 w-[52px] items-center justify-center gap-1 rounded-full bg-gray-100 text-xs font-medium text-gray-600">
                              <XCircle size={12} className="shrink-0" />
                              {eleve.mois_non_scolarises}
                            </span>
                          ) : (
                            <span className="inline-flex h-6 w-[52px] items-center justify-center text-gray-300">—</span>
                          )}
                        </td>

                        <td className="px-4 py-3 text-center">{getStatutBadge(eleve.statut)}</td>
                        <td className="px-4 py-3 text-right">
                          <p className={`font-bold ${eleve.montant_du > 0 ? 'text-red-600' : 'text-green-600'}`}>
                            {formatMontant(eleve.montant_du)}
                          </p>
                          <p className="text-xs text-gray-500">sur {formatMontant(eleve.montant_total)}</p>
                        </td>
                        {/* La cloche de relance ne s'affichait que sur les
                            eleves qui doivent quelque chose : la colonne
                            partait en dents de scie, une icone sur une ligne,
                            deux sur la suivante. L'afficher partout aurait
                            mis un bouton inerte en face de ceux qui sont a
                            jour — pire encore.
                            La relance vit desormais dans la fiche, ou l'on
                            arrive apres avoir vu ce qui est du. Il reste ici
                            un seul bouton, toujours present, et nomme : une
                            icone seule n'apprend rien a qui ne la connait
                            pas deja. */}
                        <td className="px-4 py-3 text-center">
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8 gap-1.5 border-gray-200 text-[#246990] hover:border-[#246990]/30 hover:bg-[#246990]/5"
                            onClick={() => handleViewDetails(eleve)}
                          >
                            <Eye size={14} />
                            Détails
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="mt-6 flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  {startIndex + 1} à {Math.min(endIndex, elevesFiltres.length)} sur {elevesFiltres.length}
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" 
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}>
                    Précédent
                  </Button>
                  <Button variant="outline" size="sm"
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}>
                    Suivant
                  </Button>
                </div>
              </div>

            </>
          )}
        </CardContent>
      </Card>

      <SuiviPaiementDetailsModal
        isOpen={isDetailsModalOpen}
        onClose={() => setIsDetailsModalOpen(false)}
        eleve={selectedEleveForDetails}
        anneeActive={anneeActive}
        formatMontant={formatMontant}
        onSendReminder={handleSendReminder}
        filterMois={filterMois} 
      />


      {/* ------------------------------------------------------------------
          Relancer les familles

          Le nombre annoncé ici vient du serveur, du MEME calcul que l'envoi.
          On ne montre pas un chiffre estimé à côté d'un bouton qui dépense :
          chaque SMS vers le Congo coûte 0,20 € HT.
         ------------------------------------------------------------------ */}
      <Dialog
        open={Boolean(eleveARelancer)}
        onOpenChange={(ouvert) => { if (!ouvert && !envoiEnCours) setEleveARelancer(null); }}
      >
        <DialogContent className="w-[95vw] max-w-5xl !max-h-[92vh] overflow-hidden flex flex-col p-0 gap-0">
          <DialogHeader className="flex-row items-center gap-3 space-y-0 border-b px-5 py-4 text-left">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-[#246990] text-white">
              <Bell size={18} />
            </div>
            <div className="min-w-0">
              <DialogTitle className="text-lg font-semibold text-gray-900">
                Relancer par SMS
              </DialogTitle>
              <DialogDescription className="text-sm text-gray-500">
                {eleveARelancer
                  ? `${eleveARelancer.prenom} ${eleveARelancer.nom} — ${eleveARelancer.classe_nom}`
                  : ''}
              </DialogDescription>
            </div>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto px-5 py-4">
            {apercuEnCours || !apercu ? (
              <div className="flex min-h-[180px] items-center justify-center gap-2 text-gray-500">
                <Loader2 size={20} className="animate-spin" />
                Calcul des destinataires…
              </div>
            ) : (
              <div className="space-y-4">
                {/* Un seul élève ici, donc au plus un SMS. Le compteur
                    d'élèves couverts n'aurait rien à dire ; ce qui compte,
                    c'est le texte qui partira et ce qui pourrait l'empêcher. */}
                {apercu.compte.aEnvoyer > 0 && (
                  <div className="flex items-center gap-3 rounded-lg bg-[#246990]/5 p-4">
                    <Bell size={20} className="shrink-0 text-[#246990]" />
                    <p className="text-sm text-gray-700">
                      <strong className="font-semibold text-[#246990]">
                        {mesurerSms(texteSms).segments} SMS
                      </strong>
                      {' '}sera envoyé au parent, pour{' '}
                      <strong className="font-semibold text-[#246990]">
                        {coutFcfa(mesurerSms(texteSms).segments)
                          .toLocaleString('fr-FR').replace(/\u202f|\u00a0/g, ' ')} FCFA
                      </strong>
                      {' '}(0,20 € HT l'unité).
                    </p>
                  </div>
                )}

                {/* Qui ce SMS couvre. Relancer depuis la fiche d'un enfant
                    couvre toute sa fratrie : un seul message pour le parent,
                    au lieu d'un par enfant. Encore faut-il le voir avant
                    d'envoyer. */}
                {apercu.apercu?.[0]?.couverts?.length > 0 && (
                  <div className="rounded-lg border border-gray-200">
                    <div className="flex items-baseline justify-between gap-3 border-b border-gray-200 bg-gray-50 px-4 py-2">
                      <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                        {apercu.apercu[0].famille_nom || 'Élèves couverts'}
                      </p>
                      <p className="text-xs text-gray-500">
                        à {apercu.apercu[0].destinataire || 'ce parent'}
                        {' · '}
                        <span className="font-mono">{apercu.apercu[0].numero}</span>
                      </p>
                    </div>
                    <ul>
                      {apercu.apercu[0].couverts.map((enfant, index) => (
                        <li
                          key={enfant.id}
                          className={`flex items-center justify-between gap-3 px-4 py-2 text-sm ${
                            index % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'
                          }`}
                        >
                          <span className="truncate text-gray-900">{enfant.nom}</span>
                          <span className="shrink-0 font-semibold tabular-nums text-red-600">
                            {formatMontant(enfant.du)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {apercu.compte.aEnvoyer > 0 && (() => {
                  const m = mesurerSms(texteSms);
                  const trop = m.segments > 1;
                  return (
                    <div>
                      <div className="mb-1.5 flex items-baseline justify-between gap-3">
                        <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                          Ce que recevra le parent
                        </p>
                        {/* Le compteur dit trois choses : ce qui est ecrit, ce
                            que ca occupe, ce que ca coute. La limite bascule de
                            160 a 70 des qu'un caractere sort du GSM-7 — et le
                            prix double avec elle. */}
                        <p className={`text-xs tabular-nums ${trop ? 'font-semibold text-red-700' : 'text-gray-500'}`}>
                          {m.longueur} / {m.limite}
                          {' · '}{m.segments} SMS
                          {' · '}{coutFcfa(m.segments).toLocaleString('fr-FR').replace(/\u202f|\u00a0/g, ' ')} FCFA
                        </p>
                      </div>

                      <textarea
                        rows={4}
                        value={texteSms}
                        onChange={(e) => setTexteSms(e.target.value)}
                        className={`w-full rounded-lg border p-3 font-mono text-xs leading-relaxed outline-none transition-colors ${
                          trop
                            ? 'border-red-300 bg-red-50 text-red-900 focus:ring-2 focus:ring-red-200'
                            : 'border-gray-200 bg-gray-50 text-gray-700 focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/15'
                        }`}
                      />

                      {m.interdits.length > 0 && (
                        <p className="mt-1.5 text-xs text-red-700">
                          {m.interdits.map((c) => `« ${c} »`).join(', ')}
                          {m.interdits.length > 1 ? ' ne sont pas' : " n'est pas"} dans
                          l'alphabet des SMS : la limite tombe à 70 caractères et le
                          message est facturé double. Remplacez-{m.interdits.length > 1 ? 'les' : 'le'}.
                        </p>
                      )}

                      {trop && m.interdits.length === 0 && (
                        <p className="mt-1.5 text-xs text-red-700">
                          Ce message occupe {m.segments} SMS. Raccourcissez-le de{' '}
                          {m.longueur - m.limite} caractère{m.longueur - m.limite > 1 ? 's' : ''}.
                        </p>
                      )}
                    </div>
                  );
                })()}

                {/* Les écartés ne disparaissent pas en silence : ce sont des
                    familles en retard qu'on ne peut pas joindre, et la donnée
                    est à corriger. */}
                {/* Chaque motif dit ce qui s'est passé ET ce qu'on peut y
                    faire. « 1 déjà relancé(s) cette semaine » n'apprenait ni
                    qui, ni quand, ni quoi décider. */}
                {apercu.ecartes?.numeroInvalide?.length > 0 && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                    <p className="font-semibold">Le numéro du parent est inutilisable</p>
                    <p className="mt-1">
                      {apercu.ecartes.numeroInvalide[0].parent
                        ? `${apercu.ecartes.numeroInvalide[0].parent} : `
                        : ''}
                      <span className="font-mono">
                        {apercu.ecartes.numeroInvalide[0].telephone || '(vide)'}
                      </span>
                      {' '}n'est pas un numéro que l'opérateur peut joindre.
                      Corrigez-le dans la fiche du parent, puis réessayez.
                    </p>
                  </div>
                )}

                {apercu.ecartes?.numeroPartage?.length > 0 && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                    <p className="font-semibold">Ce numéro est partagé par plusieurs familles</p>
                    <p className="mt-1">
                      <span className="font-mono">{apercu.ecartes.numeroPartage[0].numero}</span>
                      {' '}est rattaché à {apercu.ecartes.numeroPartage[0].enfants} élèves
                      de {apercu.ecartes.numeroPartage[0].nomsDeFamille} familles différentes
                      {apercu.ecartes.numeroPartage[0].exemples?.length
                        ? ` (${apercu.ecartes.numeroPartage[0].exemples.join(', ')}…)`
                        : ''}.
                      Le message dirait n'importe quoi. Vérifiez les numéros de ces fiches.
                    </p>
                  </div>
                )}

                {apercu.ecartes?.relanceRecente?.length > 0 && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                    <p className="font-semibold">Ce parent a déjà été relancé récemment</p>
                    <p className="mt-1">
                      Dernier envoi
                      {jourDeRelance(apercu.ecartes.relanceRecente[0].derniere)
                        ? ` le ${jourDeRelance(apercu.ecartes.relanceRecente[0].derniere)}`
                        : ''}.
                      Un délai de {apercu.ecartes.relanceRecente[0].delaiJours ?? 7} jours
                      évite de relancer deux fois la même famille dans la semaine.
                    </p>
                  </div>
                )}

                {apercu.compte.ecartesMessageTropLong > 0 && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                    <p className="font-semibold">Le message dépasse un SMS</p>
                    <p className="mt-1">
                      Il serait facturé double. C'est en général un nom très long :
                      signalez-le, le texte doit être raccourci.
                    </p>
                  </div>
                )}

                {apercu.compte.aEnvoyer === 0 && (
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-center text-sm text-gray-600">
                    Aucun SMS ne partira.
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter className="border-t bg-gray-50 px-5 py-4">
            <Button
              variant="outline"
              className={BOUTON_PIED}
              onClick={() => setEleveARelancer(null)}
              disabled={envoiEnCours}
            >
              Annuler
            </Button>
            {/* Bloque tant que le message depasse un SMS : on ne laisse pas
                payer double sans decision explicite. */}
            <Button
              onClick={envoyerRelance}
              disabled={
                envoiEnCours || apercuEnCours || !apercu
                || apercu.compte.aEnvoyer === 0
                || mesurerSms(texteSms).segments > 1
                || texteSms.trim().length < 10
              }
              className={`${BOUTON_PIED} border border-transparent bg-[#246990] text-white hover:bg-[#1f5a7a]`}
            >
              {envoiEnCours
                ? <><Loader2 size={16} className="animate-spin" /> Envoi…</>
                : <><Bell size={16} /> Envoyer — {coutFcfa(1).toLocaleString('fr-FR').replace(/\u202f|\u00a0/g, ' ')} FCFA</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ ...toast, show: false })}
        />
      )}

    </div>
  );
};

export default SuiviPaiementScolaire;